import React, { useState } from "react";
import { Link as LinkIcon, Play, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { Field, Text, Select, Toggle, Btn, VideoUploader, ImageUploader, fmtClock } from "@/admin/kit";

export const EMPTY_VIDEO = { title: "", youtube_url: "", visible: false, thumbnail_url: null, primary: null, widescreen: null };

const toSlot = (m, aspect) => ({
  media_id: m.id, url: m.url, poster_url: m.poster_url || null, duration: m.duration || null, size: m.size || null,
  aspect: m.aspect || aspect, original_filename: m.original_filename,
});

const SlotEditor = ({ slot, label, help, defaultAspect, testid, onChange }) => {
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(false);
  const importFromUrl = async () => {
    setImporting(true);
    try {
      const { data } = await adminApi.post("/admin/media/video-from-url", { url: importUrl }, { timeout: 900000 });
      onChange(toSlot(data, defaultAspect));
      setImportUrl("");
      toast.success(`Video imported${data.duration ? ` · ${fmtClock(data.duration)}` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  return (
    <div className="p-4 grid gap-3" style={{ border: "1px solid rgba(200,155,60,.3)", borderRadius: "var(--radius)" }} data-testid={`${testid}-slot`}>
      <VideoUploader label={label} help={help} testid={testid}
                     media={slot ? { ...slot, aspect: slot.aspect === "9:16" ? "vertical 9:16" : "widescreen 16:9" } : null}
                     onChange={(m) => onChange(toSlot(m, defaultAspect))}
                     onRemove={slot ? () => onChange(null) : undefined} />
      {slot && (
        <div>
          <Btn type="button" variant="ghost" onClick={() => setPreview((v) => !v)} data-testid={`${testid}-preview-toggle`}>
            {preview ? <EyeOff size={13} /> : <Play size={13} />} {preview ? "Hide preview" : "Preview this cut"}
          </Btn>
          {preview && (
            <div className="mt-3 mx-auto" data-testid={`${testid}-preview`}
                 style={slot.aspect === "9:16" ? { aspectRatio: "9 / 16", height: 420 } : { aspectRatio: "16 / 9", width: "100%", maxWidth: 640 }}>
              <video src={mediaUrl(slot.url)} poster={slot.poster_url ? mediaUrl(slot.poster_url) : undefined} controls playsInline preload="metadata"
                     className="w-full h-full object-contain bg-black" style={{ borderRadius: "var(--radius)" }} />
            </div>
          )}
        </div>
      )}
      {slot ? (
        <Field label="Aspect ratio" help="Detected from the file when possible — change it if the player frames the video wrong.">
          <Select value={slot.aspect || defaultAspect} onChange={(e) => onChange({ ...slot, aspect: e.target.value })} data-testid={`${testid}-aspect`}>
            <option value="9:16">9:16 vertical (phone / Reels)</option>
            <option value="16:9">16:9 widescreen (YouTube / TV)</option>
          </Select>
        </Field>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <Text value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="…or paste a direct https:// link to an .mp4" className="flex-1" data-testid={`${testid}-url`} />
          <Btn type="button" variant="ghost" onClick={importFromUrl} disabled={importing || !importUrl} data-testid={`${testid}-import`}><LinkIcon size={13} /> {importing ? "Importing…" : "Import from link"}</Btn>
        </div>
      )}
    </div>
  );
};

export const MusicVideoBlock = ({ video, artworkUrl, onChange }) => {
  const v = { ...EMPTY_VIDEO, ...(video || {}) };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const hasAny = !!(v.primary || v.widescreen || (v.youtube_url || "").trim());
  return (
    <div className="grid gap-5" data-testid="music-video-block">
      <p style={{ color: "var(--ash)", fontSize: ".75rem", marginTop: "-.5rem" }}>
        Attach the music video here, the same way you attach the audio and cover. Keep &ldquo;Video visible on site&rdquo; off until you want fans to see the Watch button.
      </p>
      <SlotEditor slot={v.primary} label="Music video file" help="MP4 preferred (also m4v, mov, webm). Large files OK — up to 500 MB." defaultAspect="9:16" testid="video-primary" onChange={(s) => set("primary", s)} />
      <SlotEditor slot={v.widescreen} label="Widescreen version (optional)" help="A second cut — usually the 16:9 version for TVs and YouTube. Fans get a Vertical / Widescreen switch when both exist." defaultAspect="16:9" testid="video-widescreen" onChange={(s) => set("widescreen", s)} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Video title"><Text value={v.title} onChange={(e) => set("title", e.target.value)} placeholder="Song Title — Official Music Video" data-testid="video-title" /></Field>
        <Field label="YouTube URL" help="Optional. Adds a “Watch on YouTube” choice next to “Watch here”."><Text value={v.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} placeholder="https://youtube.com/watch?v=…" data-testid="video-youtube" /></Field>
      </div>
      <ImageUploader label="Custom video thumbnail (optional)" help="Leave empty to use the release cover art." square={false} value={v.thumbnail_url} placeholder={artworkUrl}
                     valueCaption={`Custom thumbnail${v.thumbnail_name ? ` · ${v.thumbnail_name}` : ""}`}
                     fallbackCaption="Using release cover art (no custom thumbnail uploaded)"
                     onChange={(url, m) => onChange({ ...v, thumbnail_url: url, thumbnail_name: m?.original_filename || "" })}
                     onRemove={() => onChange({ ...v, thumbnail_url: null, thumbnail_name: "" })} testid="video-thumb" />
      <div className="pt-3" style={{ borderTop: "var(--rule)" }}>
        <Toggle checked={!!v.visible} onChange={(val) => set("visible", val)} label="Video visible on site (shows the WATCH THE VIDEO button)" testid="video-visible" />
        {v.visible && !hasAny && <p className="mt-2 font-caps" style={{ color: "#E2A03F", fontSize: ".55rem" }} data-testid="video-visible-warning">Nothing to show yet — upload a file or add a YouTube link.</p>}
        {!v.visible && hasAny && <p className="mt-2 font-caps text-ash" style={{ fontSize: ".55rem" }}>Attached but hidden. Flip this on whenever you&apos;re ready — it carries over automatically when the release goes live.</p>}
      </div>
    </div>
  );
};
